import React from 'react';
import '../assets/styles/FeatureSection.css';

interface FeatureSectionProps {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  reverse?: boolean;
  showConsultation?: boolean;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  reverse = false,
  showConsultation = false
}) => {
  // Extract the base filename without extension
  const getBasename = (path: string): string => {
    const filename = path.split('/').pop() || '';
    return filename.split('.')[0];
  };
  
  const basename = getBasename(imageSrc);
  const imageExt = imageSrc.split('.').pop() || '';
  
  // Split description into paragraphs
  const paragraphs = description.trim().split(/\n\s*\n/);
  
  return (
    <>
      <section className={`feature-section ${reverse ? 'reverse' : ''}`}>
        <div className="container">
          <div className="feature-wrapper">
            <div className="feature-content">
              <h2>{title}</h2>
              {paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph.trim()}</p>
              ))}
              
              {title.includes("Price on Rails") && (
                <ul className="feature-list">
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Real-time price movement predictions</span>
                  </li>
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>AI-powered analytics constantly fine-tuned</span>
                  </li>
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Works across stocks, futures, and crypto markets</span>
                  </li>
                </ul>
              )}
              
              {title.includes("Agentic Bot") && (
                <ul className="feature-list">
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Automated trading based on Price on Rails signals</span>
                  </li>
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Available on both desktop and web interfaces</span>
                  </li>
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Human oversight with AI-driven precision</span>
                  </li>
                </ul>
              )}
            </div>
            
            <div className="feature-image">
              <picture>
                {/* WebP format */}
                <source
                  type="image/webp"
                  srcSet={`/images/optimized/${basename}.webp`}
                />
                {/* Original format fallback */}
                <img 
                  src={imageSrc} 
                  alt={imageAlt}
                  width="540" 
                  height="360"
                  loading="lazy"
                  decoding="async"
                  style={{
                    maxWidth: '100%',
                    height: 'auto'
                  }}
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      
      {showConsultation && (
        <section className="consultation-section-wrapper" style={{backgroundColor: '#ffffff'}}>
          <div className="consultation-section">
            <div className="booking-header">
              <br />
              <h3>Schedule a <span className="highlight">Free Consultation</span></h3>
              <p>Discover how our platform can enhance your trading strategy with a personalized demo</p>
            </div>
            <div className="booking-card">
              <div className="booking-card-content">
                <div className="booking-icon">
                  <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M36.6 30.52C35.1 29.02 33.28 29.02 31.8 30.52L28.68 33.64C26.1 32.32 23.7 30.42 21.56 28.26C19.58 26.28 17.68 23.9 16.36 21.32L19.48 18.2C20.98 16.72 20.98 14.9 19.48 13.4L13.4 7.32C11.92 5.84 10.1 5.84 8.6 7.32L5.08 10.86C3.68 12.26 3.12 14.2 3.48 16.08C4.32 20.52 6.94 26.14 11.4 30.6C15.86 35.06 21.48 37.68 25.92 38.52C26.36 38.6 26.8 38.64 27.22 38.64C28.78 38.64 30.26 38.04 31.38 36.92L34.9 33.4C36.38 31.9 36.38 30.08 34.9 28.6L36.6 30.52Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Free 30-Minute Consultation</h3>
                <div className="booking-features">
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Personalized platform walkthrough</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Q&A with our trading experts</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>No obligation or pressure</span>
                  </div>
                </div>
                <div className="booking-cta">
                  <a href="https://calendly.com/kevin-translatestocks/30min" className="btn-booking">
                    Schedule Your Free Consultation
                  </a>
                  <p className="booking-note">Choose a time that works for you</p>
                </div>
              </div>
            </div>
          </div>          
        </section>
      )}
    </>
  );
};

export default FeatureSection;
